import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiX, FiSend, FiUser, FiMail, FiPhone,
  FiMessageSquare, FiCheckCircle,
} from 'react-icons/fi';
import { useModal } from '../../context/ModalContext';

const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORM_ACCESS_KEY;

const ContactModal = () => {
  const { isOpen, closeModal } = useModal();

  const emptyForm = {
    name: '', email: '', phone: '', service: '', message: '',
  };

  const [formData, setFormData] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [botField, setBotField] = useState('');
  const formLoadTime = useRef(null);
  const lastSubmitTime = useRef(0);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') closeModal(); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, closeModal]);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Track when form becomes visible
  useEffect(() => {
    if (isOpen) formLoadTime.current = Date.now();
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData(emptyForm);
      setSubmitted(false);
      setError('');
      setBotField('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot check — bots fill hidden fields
    if (botField) return;

    // Time-based check — reject if submitted too fast (<3s)
    if (formLoadTime.current && Date.now() - formLoadTime.current < 3000) {
      setError('Please take a moment before submitting.');
      return;
    }

    // Rate limit — block submissions within 10s of each other
    if (Date.now() - lastSubmitTime.current < 10000) {
      setError('Please wait before submitting again.');
      return;
    }
    lastSubmitTime.current = Date.now();

    setLoading(true);
    setError('');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ access_key: WEB3FORMS_ACCESS_KEY, botcheck: '', ...formData }),
      });
      const result = await response.json();
      if (result.success) {
        setSubmitted(true);
        setTimeout(() => closeModal(), 3000);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Failed to send. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[999] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-dark-900/70 backdrop-blur-sm"
            onClick={closeModal}
          />

          {/* Modal panel */}
          <motion.div
            className="relative bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
            initial={{ scale: 0.92, opacity: 0, y: 24 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 24 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-6 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Get a Free Quote</h2>
                <p className="text-white/80 text-sm mt-1">Our experts will get back to you within 24 hours</p>
              </div>
              <button
                onClick={closeModal}
                className="w-9 h-9 bg-white/20 hover:bg-white/30 text-white flex items-center justify-center rounded-lg transition-colors flex-shrink-0"
                aria-label="Close modal"
              >
                <FiX className="text-xl" />
              </button>
            </div>

            {/* Body */}
            <div className="p-8">
              {submitted ? (
                <div className="text-center py-10">
                  <FiCheckCircle className="text-5xl text-secondary-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-dark-800 mb-2">Thank You!</h3>
                  <p className="text-dark-500">Your message has been sent successfully. We'll contact you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Honeypot — hidden from humans, traps bots */}
                  <input
                    type="text"
                    name="botcheck"
                    value={botField}
                    onChange={(e) => setBotField(e.target.value)}
                    style={{ display: 'none' }}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="modal-name" className="flex items-center gap-2 text-sm font-semibold text-dark-700 mb-2">
                        <FiUser className="text-primary-500" /> Full Name *
                      </label>
                      <input
                        type="text"
                        id="modal-name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your name"
                        required
                        className="w-full px-4 py-3 border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all rounded-lg"
                      />
                    </div>
                    <div>
                      <label htmlFor="modal-email" className="flex items-center gap-2 text-sm font-semibold text-dark-700 mb-2">
                        <FiMail className="text-primary-500" /> Email Address *
                      </label>
                      <input
                        type="email"
                        id="modal-email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                        required
                        className="w-full px-4 py-3 border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="modal-phone" className="flex items-center gap-2 text-sm font-semibold text-dark-700 mb-2">
                        <FiPhone className="text-primary-500" /> Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="modal-phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Enter your phone number"
                        required
                        className="w-full px-4 py-3 border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all rounded-lg"
                      />
                    </div>
                    <div>
                      <label htmlFor="modal-service" className="block text-sm font-semibold text-dark-700 mb-2">
                        Service Interested In
                      </label>
                      <select
                        id="modal-service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all rounded-lg"
                      >
                        <option value="">Select a service</option>
                        <option value="solar-pump">Solar Water Pump</option>
                        <option value="rooftop">Solar Rooftop</option>
                        <option value="panel">Solar Panels</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="modal-message" className="flex items-center gap-2 text-sm font-semibold text-dark-700 mb-2">
                      <FiMessageSquare className="text-primary-500" /> Message *
                    </label>
                    <textarea
                      id="modal-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your requirements..."
                      rows="4"
                      required
                      className="w-full px-4 py-3 border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all resize-none rounded-lg"
                    ></textarea>
                  </div>

                  {error && <p className="text-red-500 text-sm">{error}</p>}

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary w-full justify-center disabled:opacity-70"
                  >
                    {loading ? 'Sending…' : <><FiSend className="inline mr-2" />Send Message</>}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default ContactModal;
